import { useState, useEffect } from 'react';
import { Head } from '@inertiajs/react';
import { useForm } from '@mantine/form';
import { Container, Title, PasswordInput, Paper, Button, Stack, Box, MantineProvider, Anchor } from '@mantine/core';
import { Notifications, showNotification } from '@mantine/notifications';
import { IconCheck, IconAlertCircle } from '@tabler/icons-react';

export default function ResetPassword({
  resetPasswordToken: propToken,
  minimumPasswordLength = 6,
}: {
  resetPasswordToken?: string;
  minimumPasswordLength?: number;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState<string | null>(propToken || null);

  useEffect(() => {
    if (!token) {
      const urlToken = new URLSearchParams(window.location.search).get('reset_password_token');
      setToken(urlToken);
    }
  }, [token]);

  const form = useForm({
    initialValues: {
      password: '',
      password_confirmation: '',
    },
    validate: {
      password: (value) =>
        value.length >= minimumPasswordLength ? null : `Password must be at least ${minimumPasswordLength} characters`,
      password_confirmation: (value, values) => (value === values.password ? null : 'Passwords do not match'),
    },
    validateInputOnChange: true,
  });

  const handleSubmit = async (values: typeof form.values) => {
    setIsLoading(true);
    const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;

    const formData = new FormData();
    formData.append('user[password]', values.password);
    formData.append('user[password_confirmation]', values.password_confirmation);
    formData.append('user[reset_password_token]', token || '');

    console.log('Reset token being sent:', token);

    try {
      const response = await fetch('/password', {
        method: 'PUT',
        headers: {
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        showNotification({
          color: 'green',
          title: 'Password Changed',
          message: 'Your password has been successfully updated.',
          icon: <IconCheck />,
          autoClose: 5000,
        });

        setTimeout(() => {
          window.location.href = '/login';
        }, 1500);
      } else {
        const data = await response.json().catch(() => ({}));
        const errorMessage = data?.error || 'Something went wrong. Please try again or request a new password reset.';

        showNotification({
          color: 'red',
          title: 'Error',
          message: errorMessage,
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });
      }
    } catch (error) {
      showNotification({
        color: 'red',
        title: 'Error',
        message: 'An unexpected error occurred.',
        icon: <IconAlertCircle />,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MantineProvider>
      <Notifications position="top-right" />
      <Head title="Change your password" />
      <Container size="xs" mt="lg" style={{ minHeight: '75vh' }}>
        <Box>
          <Paper shadow="md" p={30} radius="md" style={{ backgroundColor: '#75757530' }}>
            <Title order={2} ta="center" mb="xl" c="white" className="poppins">
              Change your password
            </Title>

            <form onSubmit={form.onSubmit(handleSubmit)}>
              <input type="hidden" name="reset_password_token" value={token || ''} />

              <Stack>
                <PasswordInput
                  label={`New password (${minimumPasswordLength} characters minimum)`}
                  placeholder="Enter new password"
                  required
                  styles={{
                    label: {
                      fontSize: 16,
                      fontWeight: 500,
                      color: '#fff',
                    },
                  }}
                  disabled={isLoading}
                  {...form.getInputProps('password')}
                />

                <PasswordInput
                  label="Confirm new password"
                  placeholder="Confirm password"
                  required
                  styles={{
                    label: {
                      fontSize: 16,
                      fontWeight: 500,
                      color: '#fff',
                    },
                  }}
                  disabled={isLoading}
                  {...form.getInputProps('password_confirmation')}
                />

                <Button type="submit" color="#ea580c" fullWidth size="lg" loading={isLoading} className="button-orange">
                  Change my password
                </Button>
              </Stack>
            </form>

            <Stack mt="md" gap="xs" className="login-links mt-4">
              <Anchor href="/login" className="text-gray-600 hover:text-orange-600 hover:underline">
                Back to login
              </Anchor>
              <Anchor href="/confirmation/new" className="text-gray-600 hover:text-orange-600 hover:underline">
                Didn't receive confirmation?
              </Anchor>
            </Stack>
          </Paper>
        </Box>
      </Container>
    </MantineProvider>
  );
}
